import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import { MEDIAPIPE_SKELETON } from './MediaPipeSkeleton';
import type {
  PoseEngine,
  PoseSource,
  RawPose,
  RawPoseResult,
  SkeletonDefinition,
} from './PoseEngine';

export interface MediaPipeOptions {
  wasmBaseUrl: string;
  modelAssetPath: string;
  numPoses?: number;
  minDetectionConfidence?: number;
  minTrackingConfidence?: number;
  minPresenceConfidence?: number;
  /** MediaPipe inference backend. Default 'GPU' (WebGL). 'CPU' (WASM
   *  SIMD) is more reliable on Android Chrome where the WebGL path
   *  occasionally drops detections. */
  delegate?: 'GPU' | 'CPU';
}

const MAX_POSES = 5;

function clampPoses(n: number): number {
  return Math.max(1, Math.min(MAX_POSES, Math.floor(n)));
}

export class MediaPipePoseEngine implements PoseEngine {
  readonly id = 'mediapipe-pose-landmarker-lite';
  readonly label = 'MediaPipe (33pt)';
  readonly skeleton: SkeletonDefinition = MEDIAPIPE_SKELETON;
  readonly supportsMultiPose = true;
  private landmarker: PoseLandmarker | null = null;
  private numPoses: number;

  constructor(private readonly opts: MediaPipeOptions) {
    this.numPoses = clampPoses(opts.numPoses ?? 1);
  }

  async initialize(): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(this.opts.wasmBaseUrl);
    this.landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.opts.modelAssetPath,
        delegate: this.opts.delegate ?? 'GPU',
      },
      runningMode: 'VIDEO',
      numPoses: this.numPoses,
      minPoseDetectionConfidence: this.opts.minDetectionConfidence ?? 0.5,
      minPosePresenceConfidence: this.opts.minPresenceConfidence ?? 0.5,
      minTrackingConfidence: this.opts.minTrackingConfidence ?? 0.5,
    });
  }

  async setNumPoses(n: number): Promise<void> {
    const next = clampPoses(n);
    if (next === this.numPoses) return;
    this.numPoses = next;
    if (!this.landmarker) return;
    await this.landmarker.setOptions({ numPoses: next });
  }

  detectForVideo(source: PoseSource, timestamp: number): RawPoseResult {
    if (!this.landmarker) {
      throw new Error('MediaPipePoseEngine not initialized');
    }
    const result = this.landmarker.detectForVideo(source, timestamp);
    const worldGroups: { x: number; y: number; z: number; visibility?: number }[][] =
      (
        result as unknown as {
          worldLandmarks?: { x: number; y: number; z: number; visibility?: number }[][];
        }
      ).worldLandmarks ?? [];
    const poses: RawPose[] = result.landmarks.map((personLandmarks, personIdx) => {
      const world = worldGroups[personIdx];
      return {
        landmarks: personLandmarks.map((lm, i) => {
          const w = world?.[i];
          return {
            x: lm.x,
            y: lm.y,
            score: lm.visibility ?? 0,
            worldX: w?.x,
            worldY: w?.y,
            worldZ: w?.z,
            worldVisibility: w?.visibility,
          };
        }),
      };
    });
    return { poses };
  }

  dispose(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}
